// @ts-nocheck
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import DOMPurify from "dompurify";
import {
  CITATION_ATTR,
  DEAD_LINK_ATTR,
  KB_PAGE_SCOPE,
  LINK_ELEMENTS,
  describeDeadLink,
  isNavigatingHref,
  kbUrlnameFromHref,
  prepareArticleHtml,
} from "../utils/kbArticleHtml";
import { ARTICLE_MESSAGES, classifyArticleResponse } from "../utils/kbArticleFetch";
import KbBanner from "./KbBanner";
import KbBrowse from "./KbBrowse";
import environment from "../config/environment";
import "./KnowledgeBaseTwo.css";

/**
 * Knowledge Base (beta) — the AVSOS-backed KB surface at /knowledge-base-2 (ACOP-100).
 *
 * Named and prefixed to match Copilot (beta) / CopilotTwo / c2-, for the same reason: this is a
 * NEW surface built alongside the existing customer Knowledge Base (an Experience Cloud site in an
 * iframe at /resources/knowledge-base), not a replacement being swapped in underneath it.
 *
 * Two modes on ONE route, chosen by the ?article= query parameter:
 *
 *   /knowledge-base-2                      the landing page — per-product glossary + paged search (ACOP-146)
 *   /knowledge-base-2?article=<urlname>    that article, full size
 *
 * A query parameter rather than a path segment because Main.tsx dispatches on exact pathname
 * equality and dashboardTitle.ts is an exact-match map; a path segment would make this the first
 * exception in both. /resources/embed?url= is the existing precedent.
 *
 * ── The invariant this page inherits ──────────────────────────────────────────────────────────
 *
 * **No click in a rendered article may leave Copilot.** That is ACOP-93's requirement for the
 * citation slide-out and it applies here for exactly the same reason — Copilot runs inside the
 * Salesforce utility-bar iframe, so a link that navigates out drops the reader onto a bare page
 * with no way back to the ERP. The article HTML goes through the same prepareArticleHtml
 * (sanitise → scope → harden, fail closed) and the same click interception. The only difference is
 * the CSS scope: KB_PAGE_SCOPE here, ARTICLE_SCOPE in the panel, because the panel can be open
 * over this page and neither stylesheet may reach the other.
 *
 * Not unit-tested directly: it imports react-router-dom, which does not resolve under
 * react-scripts' Jest (see CitationTwo's header). That is why the rules it depends on live in
 * utils/kbGlossary.ts, utils/kbArticleFetch.ts and utils/kbArticleHtml.ts, which are.
 */

/** Where an article is fetched from. The proxy builds the AVS OS path; the browser never sees it. */
function articleProxyUrl(urlname: string): string {
  return environment.BASE_URL + "copilot/avs-os/citation?article=" + encodeURIComponent(urlname);
}

/**
 * CSS.escape with a fallback — CSS.escape is absent under jsdom. Same helper as CitationTwo; the
 * value is interpolated into an [id="..."] selector, so quotes and backslashes are what matter.
 */
function escapeSelectorValue(value: string): string {
  if (typeof CSS !== "undefined" && typeof CSS.escape === "function") return CSS.escape(value);
  return String(value).replace(/["\\]/g, "\\$&");
}

/**
 * Shown when the reader asks for an article that is not in the knowledge base.
 *
 * A React subtree rather than injected markup, so it cannot contain a live link by construction —
 * the same reasoning as CitationTwo's DeadArticlePage. Routine rather than exceptional: articles
 * reference each other by urlname while the KB is still being back-filled.
 */
function ArticleNotFound({ entry, onBack }) {
  return (
    <div className="kb2-dead" data-testid="kb2-article-missing">
      <div className="kb2-dead-icon">
        {/* fa-circle-question, not fa-file-circle-question: the vendored Font Awesome 6.5.1
            build is subsetted and does not contain file-circle-question in EITHER weight, so it
            rendered as nothing here from ACOP-93 until ACOP-100 caught it. Verified by rasterising
            the glyph against an unassigned codepoint — a width check is misleading, because most FA
            glyphs are exactly 1em wide and so is the missing-glyph box. */}
        <i className="fa-regular fa-circle-question" aria-hidden />
      </div>
      <p className="kb2-dead-title">{entry.label} is not in the knowledge base</p>
      <p className="kb2-dead-body">
        This article has not been published to the knowledge base yet. Nothing is broken on your
        end.
      </p>
      {entry.urlname && <div className="kb2-dead-ref">{entry.urlname}</div>}
      <button type="button" className="kb2-btn" onClick={onBack}>
        ← Back to the Knowledge Base
      </button>
    </div>
  );
}

/** The full-size article view. */
function ArticleView({ urlname, onOpenArticle, onBack }) {
  const [html, setHtml] = useState(null);
  const [error, setError] = useState(null);
  const [dead, setDead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [title, setTitle] = useState("");
  const contentRef = useRef(null);

  /** The AVS OS path this view is showing, so a same-article fragment link can be told apart. */
  const currentPath = useMemo(() => `/api/public/knowledge/${urlname}/html`, [urlname]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setHtml(null);
    setError(null);
    setDead(null);
    setTitle("");

    fetch(articleProxyUrl(urlname), {
      credentials: "include",
      headers: { "Authorization": "Bearer " + environment.BEARER_TOKEN },
    })
      .then(async (r) => ({
        ok: r.ok,
        status: r.status,
        isJson: (r.headers.get("content-type") || "").includes("application/json"),
        body: await r.text(),
      }))
      .then((response) => {
        if (cancelled) return;

        // Identical verdicts to the slide-out's, by construction — see kbArticleFetch.ts.
        const outcome = classifyArticleResponse(response);

        if (outcome.kind === "error") {
          setError(outcome.message);
          setLoading(false);
          return;
        }

        if (outcome.kind === "dead") {
          setDead(describeDeadLink(currentPath, urlname));
          setLoading(false);
          return;
        }

        // KB_PAGE_SCOPE, not the panel's scope: the slide-out can be open over this page.
        const prepared = prepareArticleHtml(outcome.html, DOMPurify.sanitize, KB_PAGE_SCOPE);
        const doc = new DOMParser().parseFromString(prepared.html, "text/html");
        const heading = doc.querySelector("h1,h2,h3");
        if (heading?.textContent?.trim()) setTitle(heading.textContent.trim());
        setHtml(prepared.html);
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setError(ARTICLE_MESSAGES.failed);
        setLoading(false);
      });

    return () => { cancelled = true; };
  }, [urlname, currentPath]);

  /*
   * Intercept every link click in the article — the same fail-closed interception the slide-out
   * runs, for the same reason. prepareArticleHtml has already neutralised anything that could
   * navigate, so the last branch should be unreachable; it is here because "should be unreachable"
   * is not a guarantee and the cost of being wrong is the reader landing outside the ERP.
   */
  useEffect(() => {
    const el = contentRef.current;
    if (!el || !html) return undefined;

    const handler = (e) => {
      // LINK_ELEMENTS, not "a" — for a click on an image map, e.target IS the <area>.
      const a = e.target.closest(LINK_ELEMENTS);
      if (!a) return;

      // Resolved by the proxy: another article. Open it full size, staying in this reading mode.
      const citationPath = a.getAttribute(CITATION_ATTR);
      if (citationPath) {
        e.preventDefault();
        /*
         * Scroll only when the fragment belongs to the article being read. Matching the fragment
         * alone would scroll in place whenever a link to ANOTHER article named an id this one also
         * has, silently swallowing the navigation.
         */
        const hashIdx = citationPath.indexOf("#");
        if (hashIdx > -1) {
          const samePath = citationPath.slice(0, hashIdx) === currentPath.split("#")[0];
          const anchor = citationPath.slice(hashIdx + 1);
          if (samePath || hashIdx === 0) {
            const target = el.querySelector(
              `[id="${escapeSelectorValue(anchor)}"], [name="${escapeSelectorValue(anchor)}"]`
            );
            if (target) {
              target.scrollIntoView({ behavior: "smooth", block: "start" });
              return;
            }
          }
        }
        const next = kbUrlnameFromHref(citationPath);
        if (next) onOpenArticle(next);
        return;
      }

      // Marked unresolvable — by AVSOS at ingest, or by prepareArticleHtml because nothing
      // vouched for it. Either way it stays on this page.
      const missing = a.getAttribute(DEAD_LINK_ATTR);
      if (missing !== null) {
        e.preventDefault();
        setDead(describeDeadLink(missing, a.textContent || ""));
        return;
      }

      const href = a.getAttribute("href");

      // No href attribute at all — a jump target such as <a id="top" name="top">, not a link.
      if (href === null) return;

      // In-page anchor. Also swallows a bare "#", which would otherwise jump the outer page.
      if (href.startsWith("#")) {
        e.preventDefault();
        const targetId = href.slice(1);
        if (targetId) {
          const target = el.querySelector(
            `[id="${escapeSelectorValue(targetId)}"], [name="${escapeSelectorValue(targetId)}"]`
          );
          if (target) target.scrollIntoView({ behavior: "smooth", block: "start" });
        }
        return;
      }

      // Nothing above claimed it and it can still navigate. Fail closed.
      if (isNavigatingHref(href)) {
        e.preventDefault();
        setDead(describeDeadLink(href, a.textContent || ""));
      }
    };

    el.addEventListener("click", handler);
    return () => el.removeEventListener("click", handler);
  }, [html, currentPath, onOpenArticle]);

  if (dead) return <ArticleNotFound entry={dead} onBack={onBack} />;

  return (
    <div data-testid="kb2-article-view">
      <div className="kb2-article-bar">
        <button type="button" className="kb2-btn" onClick={onBack} data-testid="kb2-back">
          ← Knowledge Base
        </button>
        {title && <span className="kb2-article-title" title={title}>{title}</span>}
      </div>

      {loading && (
        <div className="kb2-loading">
          <div className="spinner-border spinner-border-sm text-secondary" role="status">
            <span className="visually-hidden">Loading…</span>
          </div>
        </div>
      )}

      {error && <p className="kb2-error" data-testid="kb2-article-error">{error}</p>}

      {html && (
        <div className="kb2-article" ref={contentRef} dangerouslySetInnerHTML={{ __html: html }} />
      )}
    </div>
  );
}

export default function KnowledgeBaseTwo({ includeInternal = false }) {
  const [searchParams, setSearchParams] = useSearchParams();

  /*
   * The one piece of state that lives in the URL. Everything else is local: the glossary query is
   * not worth a history entry, and putting it in the URL would make every keystroke a navigation.
   */
  const article = (searchParams.get("article") || "").trim();

  /* `replace` so walking a chain of articles does not bury the landing page under history. */
  const openArticle = useCallback((urlname) => {
    setSearchParams({ article: urlname }, { replace: true });
  }, [setSearchParams]);

  const backToLanding = useCallback(() => {
    setSearchParams({}, { replace: true });
  }, [setSearchParams]);

  return (
    <div className="container-fluid py-4 px-3 kb2-page" data-testid="kb2-page">
      {article && <ArticleView urlname={article} onOpenArticle={openArticle} onBack={backToLanding} />}
      {/* The glossary + search stay mounted (hidden) while an article is open, so Back returns the
          reader to the same product, search phrase, page and scroll rather than a fresh page. */}
      <div hidden={Boolean(article)} data-testid="kb2-landing">
        <KbBanner />
        <KbBrowse includeInternal={includeInternal} onOpenArticle={openArticle} />
      </div>
    </div>
  );
}
